import type { Vec2 } from "geom/vec2";
import type { AutoDimensionType } from "../autoDimensions/autoDimensionTypesEnum";
import type { DimensionRowLevel } from "../dimensions/dimensionTypesEnum";
import type { CatalogSettingsTypeLiterals } from "../doc/catalogTypes";
import type { DocEntityType } from "../doc/docEntityTypes";
import type { BaseProvenance } from "../provenance/provenanceTypes";
import type { DocSchemaEntityType, ForeignEntityId } from "./foreignEntityId";
import type { GeneratedEntityHandle } from "./generatedEntityHandleTypes";

export type DocSchemaEntityTypeOmittingCatalogSettings = Exclude<DocSchemaEntityType, CatalogSettingsTypeLiterals>;

/**
 * DocEntityHandle is a typed handle to refer to entities in the Doc. It can be hashed
 * to produce a unique id for the entity, even if it is stored in an array.
 *
 * In general, all new entities should be stored in tables at the root of the
 * doc so their handle should only have an id field.
 */
export type DocEntityHandle =
  | GroupHandle
  | ComponentHandle
  | ComponentInstanceHandle
  | DocRefHandle
  | LevelHandle
  | BlockHandle
  | RoomHandle
  | PresetHandle
  | RoomLayoutHandle
  | RoomLayoutInstanceHandle
  | StairSystemHandle
  | StairBlockHandle
  | StairSettingHandle
  | RailingHandle
  | FreeformWallHandle
  | SiteHandle
  | RailingPostHandle
  | RoofSystemHandle
  | RoofHandle
  | EdgeBehaviorHandle
  | WallPlaneFilterHandle
  | WallPlaneRuleHandle
  | WallPlaneTrimHandle
  | WallPlaneAssemblyHandle
  | RoofSideHandle
  | FoundationSystemHandle
  | FoundationHandle
  | AssetHandle
  | UnderlayHandle
  | ReferenceGeomHandle
  | ElevationHandle
  | PaletteHandle
  | SettingsHandle
  | SettingsSetHandle
  | EntitySettingsHandle
  | OptionTemplateHandle
  | OptionSetOverridesHandle
  | SheetHandle
  | DrawingRefHandle
  | OptionSetHandle
  | InstanceOptionSetHandle
  | OptionHandle
  | OptionEffectHandle
  | OptionRuleHandle
  | OptionLockHandle
  | PropInstanceHandle
  | PropAttributesHandle
  | CaseworkHandle
  | CeilingHandle
  | OpeningHandle
  | CatalogItemHandle
  | FloorHandle
  | FloorFinishHandle
  | FloorPerimeterOpeningHandle
  | AnnotationHandle
  | FacadeHandle
  | FacadeSpanStratumHandle
  | FacadeSweepStratumHandle
  | ElectricalDeviceHandle
  | ElectricalLayoutHandle
  | ElectricalRuleHandle
  | RoofOpeningHandle
  | ShapeHandle
  | EdgeHandle
  | VertexHandle
  | SiteHandle
  | SkylightHandle
  | DormerHandle
  | ModifierHandle
  | EdgeModifierHandle
  | ColumnHandle
  | ElevationViewHandle
  | SectionViewHandle
  | View3DHandle
  | PlanViewHandle
  | ProfileHandle
  | RailingSettingHandle
  | EditModeHandle
  | MeasurementHandle
  | GeneratedEntityHandle
  | AutoDimensionHandle
  | PlanSetTemplateHandle
  | ContentTemplateHandle
  | ContentTemplateInstanceHandle
  | ContentReplicatorHandle
  | LocalParameterHandle
  | GlobalParameterHandle
  | SystemParameterHandle
  | ViewportHandle
  | DataTableHandle
  | DataSequenceHandle
  | ViewTemplateHandle
  | PlanSetTemplateConfigHandle
  | LabelHandle
  | ViewOverrideHandle
  | SelectorHandle
  | ViewSelectorHandle
  | ViewTemplateOverrideHandle
  | RoofVolumeHandle
  | SiteShapeHandle
  | EffectHandle
  | DatumHandle
  | RuleBasedSelectorHandle
  | RuleBasedSelectorInstanceHandle
  | IndeterminateHandle
  | FramingComponentHandle
  | HeaderAssemblyHandle
  | FoundationAssemblyHandle
  | FoundationComponentEntityHandle
  | FrameFloorAssemblyHandle
  | SlabFloorAssemblyHandle
  | SlabComponentHandle
  | SheathingComponentHandle
  | MasonryComponentHandle
  | CipComponentHandle
  | FloorSystemHandle
  | FloorBayHandle
  | FloorBayEdgeHandle
  | FloorSplitLineHandle
  | FloorSplitLineEdgeHandle
  | CompositeLevelHandle
  | ReportableUnitDesignationHandle;

export interface GroupHandle {
  readonly type: "GROUP";
  readonly id: string;
}

export interface ComponentHandle {
  readonly type: "COMPONENT";
  readonly id: string;
}

export interface ComponentInstanceHandle {
  readonly type: "COMPONENT_INSTANCE";
  readonly id: string;
}

export interface LocalParameterHandle {
  readonly type: "LOCAL_PARAMETER";
  readonly id: string;
}

export interface GlobalParameterHandle {
  readonly type: "GLOBAL_PARAMETER";
  readonly id: string;
}

/**
 * System parameters are strictly generated and never stored in the doc.
 *
 * The `id` is universally unique and identifies the parameter entity.
 * The optional `templateId` provides context for template-scoped behavior:
 * - Retrieving template-specific default values
 * - Determining where to write updates
 * - Displaying "System Local Parameter" vs "System Global Parameter"
 *
 * When `templateId` is omitted, the parameter is treated as global.
 */
export interface SystemParameterHandle {
  readonly type: "SYSTEM_PARAMETER";
  readonly id: string;
}

export interface DocRefHandle {
  readonly type: "DOC_REF";
  readonly id: string;
}

export interface LevelHandle {
  readonly type: "LEVEL";
  readonly id: string;
}

export interface DataTableHandle {
  readonly type: "DATA_TABLE";
  readonly id: string;
}

export interface DataSequenceHandle {
  readonly type: "DATA_SEQUENCE";
  readonly id: string;
}

export interface BlockHandle {
  readonly type: "BLOCK";
  readonly id: string;
}

export interface StairSystemHandle {
  readonly type: "STAIR_SYSTEM";
  readonly id: string;
}

export interface StairBlockHandle {
  readonly type: "STAIR_BLOCK";
  readonly id: string;
}

export interface StairSettingHandle {
  readonly type: "STAIR_SETTING";
  readonly id: string;
}

export interface RailingHandle {
  readonly type: "RAILING";
  readonly id: string;
}

export interface FreeformWallHandle {
  readonly type: "FREEFORM_WALL";
  readonly id: string;
}

export interface SiteHandle {
  readonly type: "SITE";
  readonly id: string;
}

export interface RailingPostHandle {
  readonly type: "RAILING_POST";
  readonly id: string;
}

export interface ColumnHandle {
  readonly type: "COLUMN";
  readonly id: string;
  // TODO why does this maintain two handle types? This is odd and probably
  // reflects a misuse of entity handles (ie to make the UI do something specific)
  readonly parent?: RoomHandle | RoomEdgeHandle;
}

export interface FacadeHandle {
  readonly type: "FACADE";
  readonly id: string;
}

export interface FacadeSpanStratumHandle {
  readonly type: "FACADE_SPAN_STRATUM";
  readonly id: string;
}

export interface FacadeSweepStratumHandle {
  readonly type: "FACADE_SWEEP_STRATUM";
  readonly id: string;
}

export interface RoomHandle {
  readonly type: "ROOM";
  readonly id: string;
}

export interface RoomLayoutHandle {
  readonly type: "ROOM_LAYOUT";
  readonly id: string;
}

export interface RoomLayoutInstanceHandle {
  readonly type: "ROOM_LAYOUT_INSTANCE";
  readonly id: string;
}

export interface OpeningHandle {
  readonly type: "OPENING";
  readonly id: string;
}

export interface FloorHandle {
  readonly type: "FLOOR";
  readonly id: string;
}

export interface FloorFinishHandle {
  readonly type: "FLOOR_FINISH";
  readonly id: string;
}

/** A single opening loop on a floor assembly (free-form or room-derived perimeter), not a wall `OPENING`. */
export interface FloorPerimeterOpeningHandle {
  readonly type: "FLOOR_PERIMETER_OPENING";
  readonly floorId: string;
  readonly openingIndex: number;
}

export interface EdgeBehaviorHandle {
  readonly type: "EDGE_BEHAVIOR";
  readonly id: string;
}

export interface WallPlaneFilterHandle {
  readonly type: "WALL_PLANE_FILTER";
  readonly id: string;
}

export interface WallPlaneRuleHandle {
  readonly type: "WALL_PLANE_RULE";
  readonly id: string;
}

export interface WallPlaneTrimHandle {
  readonly type: "WALL_PLANE_TRIM";
  readonly id: string;
}

/** Handle to a polyline field on an entity editable in wall-plane UV space. */
export interface WallPlanePolylineHandle {
  readonly type: "WALL_PLANE_POLYLINE";
  readonly parent: DocEntityHandle;
  readonly polylineId: "path";
}

export interface WallPlaneAssemblyHandle {
  readonly type: "WALL_PLANE_ASSEMBLY";
  readonly id: string;
}

export interface PorchColumnPatternHandle {
  readonly type: "PORCH_COLUMN_PATTERN";
  readonly roomId: string;
  readonly columnPatternId: number;
}

export interface CaseworkHandle {
  readonly type: "CASEWORK";
  readonly id: string;
}

export interface CeilingHandle {
  readonly type: "CEILING";
  readonly id: string;
}

export interface PropInstanceHandle {
  readonly type: "PROP_INSTANCE";
  readonly id: string;
}

export interface PropAttributesHandle {
  readonly type: "PROP";
  readonly settingsHandle?: SettingsHandle;
  readonly id: string;
}

export interface AnnotationHandle {
  readonly type: "ANNOTATION";
  readonly id: string;
}

export interface SectionViewHandle {
  readonly type: "SECTION_VIEW";
  readonly id: string;
}

export type ElectricalHandle = ElectricalDeviceHandle | ElectricalLayoutHandle | ElectricalRuleHandle;

export interface ElectricalDeviceHandle {
  readonly type: "ELECTRICAL_DEVICE";
  readonly id: string;
}

export interface ElectricalLayoutHandle {
  readonly type: "ELECTRICAL_LAYOUT";
  readonly id: string;
}

export interface ElectricalRuleHandle {
  readonly type: "ELECTRICAL_RULE";
  readonly settingsHandle: SettingsHandle; // this is required due to duplicate rule ids across settings sets in a home
  readonly id: string;
}

export interface PresetHandle {
  readonly type: "PRESET";
  readonly id: string;
}

export interface OptionSetOverridesHandle {
  readonly type: "OPTION_SET_OVERRIDES";
  readonly id: string;
}

export interface OptionSetHandle {
  readonly type: "OPTION_SET";
  readonly id: string;
}

export interface InstanceOptionSetHandle {
  readonly type: "INSTANCE_OPTION_SET";
  readonly id: string;
}

/**
 * Crucially optionKey must refer to the key of the DocOption
 * as it is stored in DocOptionSet["options"]. Do not use
 * DocOption["id"]
 */

export interface OptionHandle {
  readonly type: "OPTION";
  readonly optionSetId: string;
  readonly optionKey: string;
}

export interface OptionRuleHandle {
  readonly type: "OPTION_RULE";
  readonly id: string;
}

export interface OptionEffectHandle {
  readonly type: "OPTION_EFFECT";
  readonly id: string;
}

export interface OptionLockHandle {
  readonly type: "OPTION_LOCK";
  readonly id: string;
}

export interface ReferenceGeomHandle {
  readonly type: "REFERENCE_GEOM";
  readonly id: string;
}

export interface ViewOverrideHandle {
  readonly type: "VIEW_OVERRIDE";
  readonly id: string;
}

export interface SelectorHandle {
  readonly type: "SELECTOR";
  readonly id: string;
}

export interface ViewSelectorHandle {
  readonly type: "VIEW_SELECTOR";
  readonly id: string;
}

export interface ViewportHandle {
  readonly type: "VIEWPORT";
  readonly id: string;
}

export interface PaletteHandle {
  readonly type: "PALETTE";
  readonly id: string;
}

export interface SettingsHandle {
  readonly type: "SETTINGS";
  readonly id: string;
}

export interface SettingsSetHandle {
  readonly type: "SETTINGS_SET";
  readonly id: string;
}

export interface EntitySettingsHandle {
  readonly type: "ENTITY_SETTINGS";
  readonly id: string;
}

export interface OptionTemplateHandle {
  readonly type: "OPTION_TEMPLATE";
  readonly id: string;
}

export interface RoofSystemHandle {
  readonly type: "ROOF_SYSTEM";
  readonly id: string;
}

export interface FloorSystemHandle {
  readonly type: "FLOOR_SYSTEM";
  readonly id: string;
}

export interface FloorBayHandle {
  readonly type: "FLOOR_BAY";
  readonly id: string;
}

export interface FloorBayEdgeHandle {
  readonly type: "EDGE";
  readonly parent: FloorBayHandle;
  readonly shapeId: "outline";
  readonly edgeId: number;
  readonly perimeterIndex: number;
  /** Stable edge ID for override lookup. Produced by generateFloorBays. */
  readonly stableEdgeId: string;
}

export interface FloorSplitLineHandle {
  readonly type: "FLOOR_SPLIT_LINE";
  readonly id: string;
}

export interface FloorSplitLineEdgeHandle {
  readonly type: "EDGE";
  readonly parent: FloorSplitLineHandle;
  readonly shapeId: "splitLine";
  readonly edgeId: number;
}

export interface RoofHandle {
  readonly type: "ROOF";
  readonly id: string;
}

export interface CatalogItemHandle {
  readonly type: "CATALOG_ITEM";
  readonly itemId: string;
}

export interface FoundationSystemHandle {
  readonly type: "FOUNDATION_SYSTEM";
  readonly id: string;
}

export interface FoundationHandle {
  readonly type: "FOUNDATION";
  readonly id: string;
}

export interface AssetHandle {
  readonly type: "ASSET";
  readonly id: string;
}

export interface UnderlayHandle {
  readonly type: "UNDERLAY";
  readonly id: string;
}

export interface ElevationHandle {
  readonly type: "ELEVATION";
  readonly id: string;
}

export type DocViewHandle = View3DHandle | PlanViewHandle | ElevationViewHandle | SectionViewHandle;

export interface View3DHandle {
  readonly type: "VIEW_3D";
  readonly id: string;
}

export interface PlanViewHandle {
  readonly type: "PLAN_VIEW";
  readonly id: string;
}

export interface ElevationViewHandle {
  readonly type: "ELEVATION_VIEW";
  readonly id: string;
}

export interface SheetHandle {
  readonly type: "SHEET";
  readonly id: string;
}

export interface DrawingRefHandle {
  readonly type: "DRAWING_REF";
  readonly id: string;
}

export interface ShapeHandleBase {
  readonly parent: ShapeBasedEntityHandle;
  readonly shapeId: string;
}

export interface ShapeHandle extends ShapeHandleBase {
  readonly type: "SHAPE";
}

export interface VertexHandle extends ShapeHandleBase {
  readonly type: "VERTEX";
  readonly vertexId: number;
}

export type ShapeComponentHandle = VertexHandle | EdgeHandle;

export interface EdgeHandle extends ShapeHandleBase {
  readonly type: "EDGE";
  readonly edgeId: number;
}

export interface RoomEdgeHandle {
  readonly type: "EDGE";
  readonly parent: RoomHandle;
  readonly shapeId: "outline";
  readonly edgeId: number;
}

export interface RailingEdgeHandle extends EdgeHandle {
  readonly parent: RailingHandle;
  readonly shapeId: "path";
}

export interface FreeformWallEdgeHandle extends EdgeHandle {
  readonly parent: FreeformWallHandle;
  readonly shapeId: "path";
}

export interface RoofSideHandle {
  readonly type: "ROOF_SIDE";
  readonly parent: RoofHandle;
  readonly side: string;
}

export interface RoofEdgeHandle {
  readonly type: "EDGE";
  readonly parent: RoofHandle;
  readonly shapeId: "outline";
  readonly edgeId: number;
}

export interface FloorEdgeHandle {
  readonly type: "EDGE";
  readonly parent: FloorHandle;
  readonly shapeId: "outline";
  readonly edgeId: number;
  readonly perimeterIndex: number;
  /** Stable edge ID for override lookup. Produced by generateFloorSystemBoundary. */
  readonly stableEdgeId?: string;
}

export interface FloorOpeningEdgeHandle {
  readonly type: "EDGE";
  readonly parent: FloorHandle;
  readonly shapeId: "opening";
  readonly edgeId: number;
  readonly openingIndex: number;
  /** Stable edge ID for override lookup. Produced by generateFloorSystemBoundary. */
  readonly stableEdgeId?: string;
}

export interface FoundationEdgeHandle {
  readonly type: "EDGE";
  readonly parent: FoundationHandle;
  readonly shapeId: "outline";
  readonly edgeId: number;
}

export interface ReferenceGeomEdgeHandle {
  readonly type: "EDGE";
  readonly parent: ReferenceGeomHandle;
  readonly shapeId: "geom";
  readonly edgeId: number;
} /**
 * Provides a mechanism to refer to a temporary edit mode state. This is important
 * as a shape can be associated with such an entity.
 */

export interface EditModeHandle {
  readonly type: "EDIT_MODE";
  readonly editModeType: "SHAPE" | "WALL_PLANE_OVERRIDE";
}

export interface RoofVolumeHandle {
  readonly type: "ROOF_VOLUME";
  readonly id: string;
}

export type ShapeBasedEntityHandle =
  | RoomHandle
  | RoofHandle
  | ReferenceGeomHandle
  | AnnotationHandle
  | StairBlockHandle
  // The children of this entity can be shape based
  | RoomLayoutInstanceHandle
  | RailingHandle
  | FreeformWallHandle
  | CeilingHandle
  | FloorFinishHandle
  | MeasurementHandle
  | ViewportHandle
  | SiteShapeHandle
  | EditModeHandle
  | FloorHandle
  | FloorBayHandle
  | FloorSplitLineHandle
  | FoundationHandle;

export type ShapeBasedEntityType = ShapeBasedEntityHandle["type"];

export interface DormerHandle {
  readonly type: "DORMER";
  readonly id: string;
}

export interface SkylightHandle {
  readonly type: "SKYLIGHT";
  readonly id: string;
}

export interface RoofOpeningHandle {
  readonly type: "ROOF_OPENING";
  readonly id: string;
}

export interface ModifierHandle {
  readonly type: "MODIFIER";
  readonly id: string;
}

export interface EdgeModifierHandle {
  readonly type: "EDGE_MODIFIER";
  readonly id: string;
}

export interface MeasurementHandle {
  readonly type: "MEASUREMENT";
  readonly id: string;
}

export interface SiteShapeHandle {
  readonly type: "SITE_SHAPE";
  readonly id: string;
  readonly parent: SiteHandle;
}
export interface PlanSetTemplateHandle {
  readonly type: "PLAN_SET_TEMPLATE";
  readonly id: string;
}

export interface PlanSetTemplateConfigHandle {
  readonly type: "PLAN_SET_TEMPLATE_CONFIG";
  readonly id: string;
}

export interface ContentTemplateHandle {
  readonly type: "CONTENT_TEMPLATE";
  readonly id: string;
}

export interface ContentTemplateInstanceHandle {
  readonly type: "CONTENT_TEMPLATE_INSTANCE";
  readonly id: string;
}

export interface ContentReplicatorHandle {
  readonly type: "CONTENT_REPLICATOR";
  readonly id: string;
}

export interface ViewTemplateHandle {
  readonly type: "VIEW_TEMPLATE";
  readonly id: string;
}

export interface LabelHandle {
  readonly type: "LABEL";
  readonly id: string;
}

export interface ViewTemplateOverrideHandle {
  readonly type: "VIEW_TEMPLATE_OVERRIDE";
  readonly id: string;
}

export interface EffectHandle {
  readonly type: "EFFECT";
  readonly id: string;
}

export interface ProfileHandle {
  readonly type: "PROFILE";
  readonly id: string;
}

export interface RailingSettingHandle {
  readonly type: "RAILING_SETTING";
  readonly id: string;
}

export interface DatumHandle {
  readonly type: "DATUM";
  readonly id: string;
}

export interface RuleBasedSelectorHandle {
  readonly type: "RULE_BASED_SELECTOR";
  readonly id: string;
}

export interface RuleBasedSelectorInstanceHandle {
  readonly type: "RULE_BASED_SELECTOR_INSTANCE";
  readonly id: string;
}

export interface IndeterminateHandle {
  readonly type: "INDETERMINATE_ENTITY_TYPE";
  readonly entityTypes: DocEntityHandle["type"][];
  readonly id: string;
}

export interface FramingComponentHandle {
  readonly type: "FRAMING_COMPONENT";
  readonly id: string;
}

export interface HeaderAssemblyHandle {
  readonly type: "HEADER_ASSEMBLY";
  readonly id: string;
}

export interface FoundationAssemblyHandle {
  readonly type: "FOUNDATION_ASSEMBLY";
  readonly id: string;
}

export interface FoundationComponentEntityHandle {
  readonly type: "FOUNDATION_COMPONENT";
  readonly id: string;
}

export interface FrameFloorAssemblyHandle {
  readonly type: "FRAME_FLOOR_ASSEMBLY";
  readonly id: string;
}

export interface SlabFloorAssemblyHandle {
  readonly type: "SLAB_FLOOR_ASSEMBLY";
  readonly id: string;
}

export interface SlabComponentHandle {
  readonly type: "SLAB_COMPONENT";
  readonly id: string;
}

export interface SheathingComponentHandle {
  readonly type: "SHEATHING_COMPONENT";
  readonly id: string;
}

export interface MasonryComponentHandle {
  readonly type: "MASONRY_COMPONENT";
  readonly id: string;
}

export interface CipComponentHandle {
  readonly type: "CIP_COMPONENT";
  readonly id: string;
}

export interface CompositeLevelHandle {
  readonly type: "COMPOSITE_LEVEL";
  readonly id: string;
}

export interface ReportableUnitDesignationHandle {
  readonly type: "REPORTABLE_UNIT_DESIGNATION";
  readonly id: string;
}

export type GroupableEntityHandle = Extract<
  DocEntityHandle,
  {
    type:
      | "ROOF_SYSTEM"
      | "ROOF"
      | "ROOF_VOLUME"
      | "FACADE"
      | "SETTINGS_SET"
      | "MODIFIER"
      | "MEASUREMENT"
      | "OPENING"
      | "FLOOR"
      | "FLOOR_FINISH"
      | "BLOCK"
      | "REFERENCE_GEOM"
      | "ANNOTATION"
      | "CASEWORK"
      | "RAILING"
      | "FREEFORM_WALL"
      | "SITE"
      | "COLUMN"
      | "STAIR_SYSTEM"
      | "STAIR_BLOCK"
      | "CEILING"
      | "PROP_INSTANCE"
      | "ELECTRICAL_LAYOUT"
      | "ELECTRICAL_DEVICE"
      | "SHEET"
      | "CONTENT_TEMPLATE"
      | "CONTENT_TEMPLATE_INSTANCE"
      | "PALETTE"
      | "DATA_SEQUENCE"
      | "DATUM"
      | "PROP"
      | "FRAMING_COMPONENT"
      | "FOUNDATION_ASSEMBLY"
      | "FOUNDATION_COMPONENT"
      | "HEADER_ASSEMBLY"
      | "WALL_PLANE_ASSEMBLY"
      | "FRAME_FLOOR_ASSEMBLY"
      | "SLAB_FLOOR_ASSEMBLY"
      | "SLAB_COMPONENT"
      | "SHEATHING_COMPONENT"
      | "MASONRY_COMPONENT"
      | "CIP_COMPONENT"
      | "GLOBAL_PARAMETER"
      | "SYSTEM_PARAMETER"
      | "PROFILE";
  }
>;

export type LockableEntityHandle = Extract<
  DocEntityHandle,
  {
    type:
      | "GROUP"
      | "LEVEL"
      | "UNDERLAY"
      | "STAIR_SYSTEM"
      | "STAIR_BLOCK"
      | "REFERENCE_GEOM"
      | "BLOCK"
      | "RAILING"
      | "ROOM"
      | "MEASUREMENT"
      | "FLOOR_SYSTEM"
      | "ROOF_SYSTEM"
      | "FOUNDATION_SYSTEM";
  }
>;
export type HidableEntityHandle = Extract<
  DocEntityHandle,
  {
    type:
      | "GROUP"
      | "LEVEL"
      | "UNDERLAY"
      | "STAIR_BLOCK"
      | "STAIR_SYSTEM"
      | "REFERENCE_GEOM"
      | "ROOF_SYSTEM"
      | "ROOF"
      | "BLOCK"
      | "ROOM"
      | "MEASUREMENT"
      | "FLOOR"
      | "FLOOR_SYSTEM"
      | "FOUNDATION"
      | "FOUNDATION_SYSTEM";
  }
>;

export type GroupableEntityType = GroupableEntityHandle["type"];

export type IsolatableEntityHandle = Extract<
  DocEntityHandle,
  {
    type: "COMPONENT";
  }
>;

export type IsolatableEntityType = IsolatableEntityHandle["type"];

/** Entities exclusively owned by one component via the componentMembers hash table. */
export type ComponentOwnedMemberEntityHandle =
  | RoomHandle
  | RoofSystemHandle
  | RoofHandle
  | FoundationSystemHandle
  | FloorSystemHandle
  | StairSystemHandle
  | RailingHandle
  | ComponentInstanceHandle
  | LevelHandle
  | BlockHandle;

/** Entities associated with one or more components via componentAssociations. */
export type ComponentAssociatedEntityHandle = OptionSetHandle;

export type ComponentMemberEntityHandle = ComponentOwnedMemberEntityHandle | ComponentAssociatedEntityHandle;

export type ComponentMemberEntityHandleReassignable = Exclude<ComponentMemberEntityHandle, LevelHandle | BlockHandle>;

/** Owned component members that may be reassigned to another component (excludes LEVEL and BLOCK). */
export type ComponentOwnedMemberEntityHandleReassignable = Exclude<
  ComponentOwnedMemberEntityHandle,
  LevelHandle | BlockHandle
>;

export type LevelHostedEntityHandle =
  | StairSystemHandle
  | RailingHandle
  | FreeformWallHandle
  | AnnotationHandle
  | FoundationSystemHandle
  | FloorSystemHandle;

export const levelHostedEntityTypes = [
  "STAIR_SYSTEM",
  "RAILING",
  "FREEFORM_WALL",
  "ANNOTATION",
  "FOUNDATION_SYSTEM",
  "FLOOR_SYSTEM",
] as const satisfies LevelHostedEntityType[];

export type LevelHostedEntityType = LevelHostedEntityHandle["type"];

/** Level-hosted types picked on the plan during component create/add (not already in componentMemberTypesWithCanvasSelection). */
export const componentSelectableLevelHostedEntityTypes = [
  "ANNOTATION",
] as const satisfies readonly LevelHostedEntityHandle["type"][];

export type ComponentOwnedMemberEntityType = ComponentOwnedMemberEntityHandle["type"];

export const componentOwnedMemberEntityTypes = [
  "ROOM",
  "ROOF_SYSTEM",
  "ROOF",
  "FOUNDATION_SYSTEM",
  "FLOOR_SYSTEM",
  "STAIR_SYSTEM",
  "RAILING",
  "COMPONENT_INSTANCE",
  "LEVEL",
  "BLOCK",
] as const satisfies readonly ComponentOwnedMemberEntityType[];

export type ComponentAssociatedEntityType = ComponentAssociatedEntityHandle["type"];

export const componentAssociatedEntityTypes = [
  "OPTION_SET",
] as const satisfies readonly ComponentAssociatedEntityType[];

export type ComponentMemberEntityType = ComponentMemberEntityHandle["type"];

export const componentMemberEntityTypes = [
  ...componentOwnedMemberEntityTypes,
  ...componentAssociatedEntityTypes,
] as const satisfies readonly ComponentMemberEntityType[];

export function isComponentOwnedMemberEntityType(type: DocEntityType): type is ComponentOwnedMemberEntityType {
  return (componentOwnedMemberEntityTypes as readonly string[]).includes(type);
}

export function isComponentOwnedMemberEntityHandle(
  handle: DocEntityHandle,
): handle is ComponentOwnedMemberEntityHandle {
  return isComponentOwnedMemberEntityType(handle.type);
}

export function isComponentAssociatedEntityHandle(handle: DocEntityHandle): handle is ComponentAssociatedEntityHandle {
  return (componentAssociatedEntityTypes as readonly string[]).includes(handle.type);
}

/** Component member entity types that have no canvas selection mode (managed via inspector/explorer). */
const COMPONENT_MEMBER_TYPES_WITHOUT_SELECTION_MODE: readonly ComponentMemberEntityType[] = [
  "OPTION_SET",
  "LEVEL",
  "BLOCK",
  "ROOF_SYSTEM",
  "FOUNDATION_SYSTEM",
  "FLOOR_SYSTEM",
] as const;

const COMPONENT_MEMBER_TYPES_NON_REASSIGNABLE: readonly ComponentMemberEntityType[] = ["LEVEL", "BLOCK"] as const;

/** Component member entity types that can be selected on the canvas (have a SelectionMode). */
export type ComponentMemberTypeWithSelectionMode = Exclude<
  ComponentMemberEntityType,
  (typeof COMPONENT_MEMBER_TYPES_WITHOUT_SELECTION_MODE)[number]
>;

/** Component member entity types that can be reassigned to another component */
export type ComponentMemberEntityTypeReassignable = Exclude<
  ComponentMemberEntityType,
  (typeof COMPONENT_MEMBER_TYPES_NON_REASSIGNABLE)[number]
>;

export function isComponentMemberTypeWithSelectionMode(
  value: ComponentMemberEntityType,
): value is ComponentMemberTypeWithSelectionMode {
  return !COMPONENT_MEMBER_TYPES_WITHOUT_SELECTION_MODE.includes(value);
}

export function isComponentMemberTypeReassignable(
  value: ComponentMemberEntityType,
): value is ComponentMemberEntityTypeReassignable {
  return !COMPONENT_MEMBER_TYPES_NON_REASSIGNABLE.includes(value);
}

/** Component member types that can be picked on the plan canvas (excludes explorer-only members). */
export const componentMemberTypesWithCanvasSelection: readonly ComponentMemberTypeWithSelectionMode[] =
  componentMemberEntityTypes.filter((t): t is ComponentMemberTypeWithSelectionMode =>
    isComponentMemberTypeWithSelectionMode(t),
  );

export interface RoomEdge {
  readonly roomId: ForeignEntityId<"ROOM">;
  readonly edgeId: string;
}
export interface LevelRoomEdge {
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly roomId: ForeignEntityId<"ROOM">;
  readonly edgeId: string;
  readonly entityLocation?: boolean;
}

// FLOOR PLAN EXTERIOR
export interface AutoDimensionFloorPlanExteriorByOrientationProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION;
  readonly levelId: ForeignEntityId<"LEVEL" | "COMPOSITE_LEVEL">;
  readonly orientation: EdgeOrientation;
  readonly rowLevel: DimensionRowLevel;
}

export interface AutoDimensionFloorPlanExteriorByRoomMultipleProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly rowLevel: DimensionRowLevel;
  readonly isOrthogonal: boolean;
  readonly roomEdges: RoomEdge[];
}

/**
 * This is extracted from the `AutoDimensionFloorPlanExteriorByRoomMultipleProvenance` to represent a single room edge.
 */
export interface AutoDimensionFloorPlanExteriorByRoomSingleProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly rowLevel: DimensionRowLevel;
  readonly isOrthogonal: boolean;
  readonly roomEdge: RoomEdge;
}

// FOUNDATION EXTERIOR
export interface AutoDimensionFoundationExteriorByOrientationProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly orientation: EdgeOrientation;
  readonly rowLevel: DimensionRowLevel;
}

export interface AutoDimensionFoundationExteriorByRoomMultipleProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE;
  readonly rowLevel: DimensionRowLevel;
  readonly isOrthogonal: boolean;
  readonly roomEdges: LevelRoomEdge[];
}

/**
 * This is extracted from the `AutoDimensionFoundationExteriorByRoomMultipleProvenance` to represent a single room edge.
 */
export interface AutoDimensionFoundationExteriorByRoomSingleProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE;
  readonly rowLevel: DimensionRowLevel;
  readonly isOrthogonal: boolean;
  readonly roomEdge: LevelRoomEdge;
}

// FOUNDATION INTERIOR
export interface AutoDimensionFoundationInteriorByOrientationProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly orientation: EdgeOrientation;
  readonly rowLevel: DimensionRowLevel;
}

export interface AutoDimensionFoundationInteriorByRoomMultipleProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE;
  readonly rowLevel: DimensionRowLevel;
  readonly isOrthogonal: boolean;
  readonly roomEdges: LevelRoomEdge[];
}

/**
 * This is extracted from the `AutoDimensionFoundationInteriorByRoomMultipleProvenance` to represent a single room edge.
 */
export interface AutoDimensionFoundationInteriorByRoomSingleProvenance extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE;
  readonly rowLevel: DimensionRowLevel;
  readonly isOrthogonal: boolean;
  readonly roomEdge: LevelRoomEdge;
}

// FLOOR PLAN INTERIOR
export interface AutoDimensionFloorPlanInteriorByRoomEdgePair extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly roomEdgePairs: [RoomEdge, RoomEdge][];
}

export interface AutoDimensionFloorPlanInteriorByOrthogonalRoomEdge extends BaseProvenance {
  readonly type: AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE;
  readonly levelId: ForeignEntityId<"LEVEL">;
  readonly roomEdges: RoomEdge[];
}

export enum EdgeOrientation {
  TOP = "TOP",
  BOTTOM = "BOTTOM",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export const EdgeOrientations: {
  [key in EdgeOrientation]: Vec2;
} = {
  TOP: [1, 0],
  BOTTOM: [-1, 0],
  RIGHT: [0, -1],
  LEFT: [0, 1],
};

export const EdgeOrientationsArr = Object.entries(EdgeOrientations);

/**
 * This collection includes both runtime and persisted handles. Hashing runtime and persisted handles can produce the same hash.
 */
export type AutoDimensionHandle =
  // Floor Plan Exterior
  | AutoDimensionFloorPlanExteriorByRoomSingleHandle
  | AutoDimensionFloorPlanExteriorByRoomMultipleHandle
  | AutoDimensionFloorPlanExteriorByOrientationHandle
  // Foundation Exterior
  | AutoDimensionFoundationExteriorByRoomSingleHandle
  | AutoDimensionFoundationExteriorByRoomMultipleHandle
  | AutoDimensionFoundationExteriorByOrientationHandle
  // Foundation Interior
  | AutoDimensionFoundationInteriorByRoomSingleHandle
  | AutoDimensionFoundationInteriorByRoomMultipleHandle
  | AutoDimensionFoundationInteriorByOrientationHandle
  // Floor Plan Interior
  | AutoDimensionFloorPlanInteriorByRoomEdgePairHandle
  | AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle;

/**
 * This collection is the handle during runtime and hashing for entity selection, etc.
 */
export type AutoDimensionRuntimeHandle =
  // Floor Plan Exterior
  | AutoDimensionFloorPlanExteriorByRoomMultipleHandle
  | AutoDimensionFloorPlanExteriorByOrientationHandle
  // Foundation Exterior
  | AutoDimensionFoundationExteriorByRoomMultipleHandle
  | AutoDimensionFoundationExteriorByOrientationHandle
  // Foundation Interior
  | AutoDimensionFoundationInteriorByRoomMultipleHandle
  | AutoDimensionFoundationInteriorByOrientationHandle
  // Floor Plan Interior
  | AutoDimensionFloorPlanInteriorByRoomEdgePairHandle
  | AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle;

/**
 * This collection is for the selectors in the doc, which break multiple edge handles into individual single edge handles.
 */
export type AutoDimensionPersistedHandle =
  // Floor Plan Exterior
  | AutoDimensionFloorPlanExteriorByRoomSingleHandle
  | AutoDimensionFloorPlanExteriorByOrientationHandle
  // Foundation Exterior
  | AutoDimensionFoundationExteriorByRoomSingleHandle
  | AutoDimensionFoundationExteriorByOrientationHandle
  // Foundation Interior
  | AutoDimensionFoundationInteriorByRoomSingleHandle
  | AutoDimensionFoundationInteriorByOrientationHandle
  // Floor Plan Interior
  | AutoDimensionFloorPlanInteriorByRoomEdgePairHandle
  | AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle;

// Floor Plan Exterior
export interface AutoDimensionFloorPlanExteriorByRoomSingleHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFloorPlanExteriorByRoomSingleProvenance;
}

export interface AutoDimensionFloorPlanExteriorByRoomMultipleHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFloorPlanExteriorByRoomMultipleProvenance;
}

export interface AutoDimensionFloorPlanExteriorByOrientationHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFloorPlanExteriorByOrientationProvenance;
}

// Foundation Exterior
export interface AutoDimensionFoundationExteriorByRoomSingleHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFoundationExteriorByRoomSingleProvenance;
}

export interface AutoDimensionFoundationExteriorByRoomMultipleHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFoundationExteriorByRoomMultipleProvenance;
}

export interface AutoDimensionFoundationExteriorByOrientationHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFoundationExteriorByOrientationProvenance;
}

// Foundation Interior
export interface AutoDimensionFoundationInteriorByRoomSingleHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFoundationInteriorByRoomSingleProvenance;
}

export interface AutoDimensionFoundationInteriorByRoomMultipleHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFoundationInteriorByRoomMultipleProvenance;
}

export interface AutoDimensionFoundationInteriorByOrientationHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFoundationInteriorByOrientationProvenance;
}

// Floor Plan Interior
export interface AutoDimensionFloorPlanInteriorByRoomEdgePairHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFloorPlanInteriorByRoomEdgePair;
}

export interface AutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle {
  readonly type: "AUTO_DIMENSION";
  readonly provenance: AutoDimensionFloorPlanInteriorByOrthogonalRoomEdge;
}

export const bakedDimensionViewOverrideDefaultId = "defaultBakedDimensionViewOverride";
export const bakedDimensionViewOverrideDefaultName = "Baked Dimensions";
